import { createFileRoute, Link } from "@tanstack/react-router";
import * as m from "~/paraglide/messages";
import { JsonLd } from "~/components/seo/JsonLd";
import { breadcrumbSchema, webPageSchema, schemaGraph } from "~/lib/seo/schema";

export const Route = createFileRoute("/impressum")({
  component: ImpressumPage,
});

function ImpressumPage() {
  const jsonLd = schemaGraph(
    webPageSchema({
      name: "Impressum — Alchema",
      description:
        "Legal notice (Impressum) for Alchema, operated by EliteX GbR. Contact details, registration, and liability information.",
      url: "/impressum",
    }),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Impressum", url: "/impressum" },
    ]),
  );

  return (
    <div
      className="mx-auto max-w-3xl px-6 py-16"
      style={{ color: "var(--text)" }}
    >
      <JsonLd json={jsonLd} />
      <h1
        className="text-3xl font-bold tracking-tight"
        style={{ color: "var(--text)" }}
      >
        {m.impressum_title()}
      </h1>
      <p
        className="mt-2 text-sm"
        style={{ color: "var(--text-muted)" }}
      >
        {m.impressum_legal_basis()}
      </p>

      {/* Company Information */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.impressum_company_title()}
        </h2>
        <address
          className="mt-3 text-sm not-italic leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          EliteX GbR
          <br />
          Vaisakhan Arackal Sanu &amp; Chris Zanfir (Gesellschafter)
          <br />
          <br />
          {m.impressum_address_label()}
          <br />
          {m.impressum_address_placeholder()}
          <br />
          <br />
          {m.impressum_director_label()}
          <br />
          {m.impressum_director_name()}
        </address>
      </section>

      {/* Registration */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.impressum_registration_title()}
        </h2>
        <div
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <p>{m.impressum_hrb_pending()}</p>
          <p className="mt-2">{m.impressum_ust_pending()}</p>
        </div>
      </section>

      {/* Wirtschafts-Identifikationsnummer */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.impressum_wirtschaftsid_title()}
        </h2>
        <div
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <p>{m.impressum_wirtschaftsid_text()}</p>
        </div>
      </section>

      {/* Contact */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.impressum_contact_title()}
        </h2>
        <div
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <p>
            E-Mail:{" "}
            <a
              href="mailto:contact@alchema.eu"
              className="underline underline-offset-2"
              style={{ color: "var(--accent)" }}
            >
              contact@alchema.eu
            </a>
          </p>
          <p className="mt-1">
            {m.impressum_phone_label()}{" "}
            <a
              href="tel:+4915563148598"
              className="underline underline-offset-2"
              style={{ color: "var(--accent)" }}
            >
              {m.impressum_phone_number()}
            </a>
          </p>
        </div>
      </section>

      {/* Responsible for Content */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.impressum_content_responsible_title()}
        </h2>
        <div
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <p>{m.impressum_content_responsible_text()}</p>
          <p className="mt-1">{m.impressum_director_name()}</p>
          <p className="mt-1">{m.impressum_content_responsible_address()}</p>
        </div>
      </section>

      {/* Dispute Resolution (VSBG S36) */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.impressum_dispute_title()}
        </h2>
        <div
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <p>{m.impressum_dispute_text()}</p>
        </div>
      </section>

      {/* AI Transparency */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.impressum_ai_transparency_title()}
        </h2>
        <div
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <p>{m.impressum_ai_transparency_text()}</p>
        </div>
      </section>

      {/* Liability for Content */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.impressum_liability_content_title()}
        </h2>
        <div
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <p>{m.impressum_liability_content_text()}</p>
        </div>
      </section>

      {/* Liability for Links */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.impressum_liability_links_title()}
        </h2>
        <div
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <p>{m.impressum_liability_links_text()}</p>
        </div>
      </section>

      {/* Copyright */}
      <section className="mt-10">
        <h2 className="text-xl font-semibold" style={{ color: "var(--text)" }}>
          {m.impressum_copyright_title()}
        </h2>
        <div
          className="mt-3 text-sm leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <p>{m.impressum_copyright_text()}</p>
        </div>
      </section>

      {/* Date */}
      <p
        className="mt-10 text-sm"
        style={{ color: "var(--text-muted)" }}
      >
        {m.impressum_date()}
      </p>

      {/* Back to home */}
      <div className="mt-16">
        <Link
          to="/"
          className="text-sm underline underline-offset-2"
          style={{ color: "var(--accent)" }}
        >
          {m.impressum_back_home()}
        </Link>
      </div>
    </div>
  );
}
